import { useState } from "react";
import { toast } from "sonner";
import { Send, X } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { fmtDate } from "../lib/fmt";
import { Section } from "./Common";

const STATUS_OPTS = [
  ["ontrack", "On track — sesuai rencana"],
  ["delay", "Delay — mundur, tapi masih jalan"],
  ["blocked", "Blocked — berhenti, butuh keputusan/bantuan"],
];

const initial = (r) => ({
  status: r?.status || "ontrack",
  activity: r?.activity || "",
  kpi: Object.fromEntries(Object.entries(r?.kpi || {}).map(([k, v]) => [k, String(v)])),
  metrics: r?.metrics || "",
  blocker: r?.blocker || "",
  tomorrow: r?.tomorrow || "",
  program: r?.program || "",
});

const Area = ({ id, label, hint, value, onChange, rows = 4, placeholder }) => (
  <div>
    <label className="label" htmlFor={id}>{label}</label>
    {hint && <p className="-mt-1 mb-1.5 text-xs text-[#7a6667]">{hint}</p>}
    <textarea id={id} data-testid={`${id}-input`} rows={rows} className="field resize-y leading-relaxed" value={value}
      onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
  </div>
);

export default function ReportForm({ data, editReport, onSaved, onCancelEdit }) {
  const me = data.me;
  const base = editReport || data.myToday;
  const [f, setF] = useState(() => initial(base));
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const ds = editReport ? editReport.date : data.today;
  const codeText = base?.code || `${data.nextCodes[me.prefix]} (berikutnya)`;
  const fields = [...me.kpiFields];
  Object.keys(base?.kpi || {}).forEach((k) => {
    if (!fields.find((x) => x.key === k)) fields.push({ key: k, label: k });
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.activity.trim()) return toast.error("Aktivitas wajib diisi");
    setBusy(true);
    try {
      const r = await api.post("/reports", { date: ds, ...f });
      const msg = { created: "Laporan tersimpan", updated: "Laporan diperbarui", edited: "Perbaikan tersimpan" }[r.data.mode];
      toast.success(`${msg} · ${r.data.report.code}`);
      onSaved(r.data.report);
    } catch (ex) {
      toast.error(errMsg(ex));
    } finally {
      setBusy(false);
    }
  };

  const title = editReport ? `Edit laporan ${fmtDate(ds)}` : data.myToday ? "Laporan hari ini (sudah terisi)" : "Isi laporan hari ini";
  return (
    <Section id="form" eyebrow={editReport ? "Mode edit" : "Isi Laporan"} title={title}>
      <form onSubmit={submit} data-testid="report-form" className={`card p-4 sm:p-6 ${editReport ? "border-brand ring-2 ring-brand/15" : ""}`}>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <span className="label">Tanggal</span>
            <div data-testid="report-date-display" className="field flex items-center bg-brand-soft font-semibold">{fmtDate(ds)}</div>
          </div>
          <div>
            <span className="label">Kode aktivitas</span>
            <div data-testid="report-code-display" className="field flex items-center bg-brand-soft font-extrabold text-brand">{codeText}</div>
          </div>
          <div>
            <label className="label" htmlFor="report-program">Program</label>
            <select id="report-program" data-testid="report-program-select" className="field" value={f.program} onChange={(e) => set("program")(e.target.value)}>
              <option value="">— Tanpa program —</option>
              {[...new Set([...(data.programs || []), ...(f.program ? [f.program] : [])])].map((pg) => <option key={pg} value={pg}>{pg}</option>)}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="report-status">Status</label>
            <select id="report-status" data-testid="report-status-select" className="field" value={f.status} onChange={(e) => set("status")(e.target.value)}>
              {STATUS_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <Area id="report-activity" label="Aktivitas hari ini" hint="Satu item per baris." value={f.activity} onChange={set("activity")} rows={5}
              placeholder={"Briefing konten minggu depan\nFollow up vendor POSM"} />
          </div>
        </div>
        {fields.length > 0 && (
          <div className="mt-4">
            <span className="label">KPI hari ini</span>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {fields.map((k) => (
                <div key={k.key}>
                  <label className="mb-1 block text-xs text-[#7a6667]" htmlFor={`kpi-${k.key}`}>{k.label}</label>
                  <input id={`kpi-${k.key}`} data-testid={`kpi-input-${k.key}`} className="field tabular-nums" inputMode="numeric" placeholder="–"
                    value={f.kpi[k.key] || ""} onChange={(e) => setF((p) => ({ ...p, kpi: { ...p.kpi, [k.key]: e.target.value } }))} />
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="label" htmlFor="report-metrics">Catatan angka lain</label>
            <input id="report-metrics" data-testid="report-metrics-input" className="field" value={f.metrics} onChange={(e) => set("metrics")(e.target.value)} placeholder="Opsional" />
          </div>
          <Area id="report-blocker" label="Blocker" hint="Kosongkan bila tidak ada. Akan jadi tiket blocker." value={f.blocker} onChange={set("blocker")} rows={3} />
          <Area id="report-tomorrow" label="Rencana besok" hint="Satu item per baris." value={f.tomorrow} onChange={set("tomorrow")} rows={3} />
        </div>
        <div className="mt-5 flex flex-wrap gap-3">
          <button data-testid="report-submit-button" disabled={busy} className="btn-primary flex-1 sm:flex-none">
            <Send size={16} /> {editReport ? "Simpan perbaikan" : data.myToday ? "Perbarui laporan" : "Kirim laporan"}
          </button>
          {editReport && (
            <button type="button" data-testid="report-cancel-edit-button" onClick={onCancelEdit} className="btn-ghost"><X size={16} /> Batal</button>
          )}
        </div>
      </form>
    </Section>
  );
}
